import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { CSeverity, NativeProps } from '@/src/components/interfaces';
import { CFlex, FlexDirection } from '@/src/components/c-flex/CFlex';
import { CFieldLabel, CFieldSecurity } from '@/src/components/c-field-label/CFieldLabel';
import { CFieldValidation, FieldValidation } from '@/src/components/c-field-validations/CFieldValidation';
import { guid } from '@/src/infrastructure/guid/guid';
import { CFieldAutoCapitalize, CFieldAutoCorrect, CFieldEnterKeyHint, CFieldInputMode } from '@/src';

export enum CCurrency {
  PLN = 'PLN',
  EUR = 'EUR',
  GBP = 'GBP',
  USD = 'USD',
}

const CURRENCY_SYMBOLS: Record<CCurrency, string> = {
  [CCurrency.PLN]: 'zł',
  [CCurrency.EUR]: '€',
  [CCurrency.GBP]: '£',
  [CCurrency.USD]: '$',
};

export interface CMoneyFieldProps extends NativeProps {
  value?: number;
  currency?: CCurrency;
  currencyLocked?: boolean;
  decimalPlaces?: number;
  onChange: (value: number | undefined, currency: CCurrency) => void;
  onCurrencyChange?: (currency: CCurrency) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  security?: CFieldSecurity;
  validations?: FieldValidation[];
  hideMessageContainer?: boolean;
  inputMode?: CFieldInputMode.Numeric | CFieldInputMode.Decimal;
  enterKeyHint?: CFieldEnterKeyHint;
}

export function CMoneyField(props: CMoneyFieldProps): React.JSX.Element {
  const [id] = useState(() => guid('money-field'));
  const currency = props.currency ?? CCurrency.PLN;
  const decimalPlaces = props.decimalPlaces ?? 2;
  const inputMode = props.inputMode ?? CFieldInputMode.Numeric;

  const status = useMemo(() => {
    const hasErrors = props.validations?.some(v => v.severity === CSeverity.Error) ?? false;
    const hasWarnings = props.validations?.some(v => v.severity === CSeverity.Warning) ?? false;
    if (hasErrors) return 'error';
    if (hasWarnings) return 'warning';
    return 'neutral';
  }, [props.validations]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      props.onChange(undefined, currency);
      return;
    }
    // Ignore input if more decimal places than allowed
    const parts = raw.split('.');
    if (parts.length > 1 && parts[1].length > decimalPlaces) return;
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      props.onChange(parsed, currency);
    }
  }, [props.onChange, currency, decimalPlaces]);

  const handleCurrencyChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const newCurrency = e.target.value as CCurrency;
    props.onCurrencyChange?.(newCurrency);
    props.onChange(props.value, newCurrency);
  }, [props.onChange, props.onCurrencyChange, props.value]);

  return (
    <CFlex
      id={props.id}
      direction={FlexDirection.Column}
      class={classNames('c-money-field', {
        'c-money-field--disabled': props.disabled,
        'c-money-field--full-width': props.fullWidth,
        [`c-money-field--status-${status}`]: status !== 'neutral',
      }, props.class)}
      style={props.style}
    >
      {props.label && (
        <CFieldLabel htmlFor={`${id}-input`} label={props.label} required={props.required} security={props.security}/>
      )}

      <div className="c-money-field__container">
        <select
          className="c-money-field__currency"
          value={currency}
          onChange={handleCurrencyChange}
          disabled={props.disabled || props.currencyLocked}
        >
          {Object.values(CCurrency).map(c => (
            <option key={c} value={c}>{CURRENCY_SYMBOLS[c]}</option>
          ))}
        </select>
        <input
          id={`${id}-input`}
          className="c-money-field__input"
          type="number"
          step={1 / Math.pow(10, decimalPlaces)}
          value={props.value ?? ''}
          placeholder={props.placeholder}
          disabled={props.disabled}
          required={props.required}
          inputMode={inputMode}
          enterKeyHint={props.enterKeyHint}
          autoCapitalize={CFieldAutoCapitalize.Off}
          autoCorrect={CFieldAutoCorrect.Off}
          onChange={handleChange}
        />
      </div>

      {props.validations && props.validations.length > 0 && !props.hideMessageContainer && (
        <CFieldValidation validations={props.validations}/>
      )}
    </CFlex>
  );
}
